import type { RendererProps } from "../core/element-registry";
import { useRenderContext } from "../core/render-context";
import { cn } from "../lib/utils";
import { finiteNumber } from "./property-value-utils";

/**
 * Inline donut ring progress indicator (16px default).
 * Matches shadcn/ui v4 usage-card pattern.
 *
 * Props on element:
 *   value: 0-100 percentage (number or string)
 *   size:  pixel size (default 16)
 *   className: Tailwind utility classes (color via text-*, etc.)
 */
export function RingProgressRenderer({ element }: RendererProps) {
  return (
    <CircularProgress
      element={element}
      defaultSize={16}
      atomType="ring-progress"
    />
  );
}

export function RadialProgressRenderer({ element }: RendererProps) {
  return (
    <CircularProgress
      element={element}
      defaultSize={48}
      atomType="radial-progress"
    />
  );
}

function CircularProgress({
  element,
  defaultSize,
  atomType,
}: {
  element: RendererProps["element"];
  defaultSize: number;
  atomType: "radial-progress" | "ring-progress";
}) {
  const { resolveSymbol } = useRenderContext();
  const rawValue =
    element.value !== undefined ? resolveSymbol(String(element.value)) : 0;
  const rawMax =
    element.max !== undefined ? resolveSymbol(String(element.max)) : 100;
  const rawSize =
    element.size !== undefined
      ? resolveSymbol(String(element.size))
      : defaultSize;
  const max = Math.max(1, finiteNumber(rawMax, 100));
  const value = Math.max(0, Math.min(max, finiteNumber(rawValue, 0)));
  const size = Math.max(1, finiteNumber(rawSize, defaultSize));
  const r = 42.5;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - value / max);

  return (
    <svg
      role="progressbar"
      aria-label={element.ariaLabel ?? "Progress"}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      data-atom-type={atomType}
      data-value={String(value)}
      data-max={String(max)}
      data-size={String(size)}
      fill="none"
      height={size}
      width={size}
      strokeWidth={2}
      viewBox="0 0 100 100"
      className={cn("-rotate-90", element.className)}
    >
      <circle
        cx={50}
        cy={50}
        r={r}
        strokeWidth={12}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="opacity-20"
        stroke="currentColor"
        style={{ strokeDasharray: `${circumference}, ${circumference}` }}
      />
      <circle
        cx={50}
        cy={50}
        r={r}
        strokeWidth={12}
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke="currentColor"
        style={{
          strokeDasharray: `${circumference}, ${circumference}`,
          strokeDashoffset: offset,
        }}
      />
    </svg>
  );
}
